import Controller from './Controller';
import { PERMISSION_TYPE, LEGO_CONSTANTS, SCHEMA_TYPE, ENTITY_FIELD_TYPE } from 'constants/index';
import {
  CommonUtils,
  withRouteType,
  buildNativeDropdownChoicesFilterParams,
  mapNativeDropdownChoicesResponse,
  getLookupChoicesForField,
  findChoiceValueById,
  findDependentChoiceValuePathById,
  normalizeDependentLookupRecordData,
} from 'utils';
import { shouldUseTypeBasedEntityApi } from 'utils/entityIdentifier';
import nativeObjectConfigs from 'configs/nativeObjectConfigs';
import { getNativeObjectIDForConfigType } from 'components/RecordPage/RecordDetails/Widgets/utils/util';
export default class EntityController extends Controller {
  __getEntity = async id => {
    if (!this.hasEntityPermission(PERMISSION_TYPE.READ)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_SCHEMA_READ_PERMISSION);
    return await this.entityService.get({ id });
  };

  getEntity = async id => {
    try {
      const entity = await this.__getEntity(id);
      return this.buildResponse(true, withRouteType(entity, id));
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error);
    }
  };

  // Native-dropdown/choice-source data is fetched by the host application, not called directly from here
  // (same reasoning as GET_NATIVE_OBJECT_FIELDS/BULK_GET_NATIVE_OBJECT/SEARCH_NATIVE_OBJECT) - injected by
  // whichever component sets up this controller (EntityPage.jsx/record-page-context.js), via
  // useMessageChannelAdapter's getNativeDropdownChoices etc.
  get nativeDropdownDataSource() {
    return this._nativeDropdownDataSource;
  }

  set nativeDropdownDataSource(value) {
    this._nativeDropdownDataSource = value;
  }

  // fetches the selectable choices for lookup-backed dropdown fields (DROPDOWN_LOOKUP / DROPDOWN_LOOKUP_SECTION)
  // using each field's field_options.lego_lookup_config, so they can be rendered as the field's actual
  // selectable options instead of an unresolved lookup id. Shared by both the records list and record forms.
  fetchNativeDropdownChoices = async fields => {
    const filterParams = buildNativeDropdownChoicesFilterParams(fields);
    if (!Object.keys(filterParams).length) {
      return this.buildResponse(true, new Map());
    }

    try {
      const { success, response, errorMessage } = await this.nativeDropdownDataSource.getNativeDropdownChoices(filterParams);
      if (!success) throw new Error(errorMessage);
      return this.buildResponse(true, mapNativeDropdownChoicesResponse(response));
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error);
    }
  };

  __getWidgetCustomizationForEntityId = async id => {
    if (!this.hasEntityPermission(PERMISSION_TYPE.READ)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_SCHEMA_READ_PERMISSION);
    return await this.entityService.customisedWidgets({ id });
  };

  getWidgetCustomizationForEntityId = async id => {
    try {
      const response = await this.__getWidgetCustomizationForEntityId(id);
      return this.buildResponse(true, response);
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error);
    }
  };

  getAssociatedEntites = async entity => {
    try {
      const relatedEntityIds = this.getCustomRelatedEntityIds(entity);
      const promises = [];
      for (let index = 0; index < relatedEntityIds.length; index++) promises.push(this.__getEntity(relatedEntityIds[index]));
      const response = await Promise.allSettled(promises);
      const associatedEntites = response.filter(res => res.status === 'fulfilled').map(res => res.value);
      return this.buildResponse(true, associatedEntites);
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error);
    }
  };

  __getRelatedEntityIds = entity => {
    return this.getRelationshipFields(entity).map(field => field.related_entity_id);
  };

  getFieldLookup = entity => {
    return entity?.fields.reduce((map, field) => {
      return { ...map, [field.name]: field };
    }, {});
  };

  getCustomRelatedEntityIds = entity => {
    const set = new Set(this.getCustomRelationshipFields(entity)?.map(field => field.related_entity_id));
    return Array.from(set);
  };

  getRelationshipFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.RELATIONSHIP);
  };

  getNativeRelationshipFields = entity => {
    return this.getRelationshipFields(entity)?.filter(field => field.field_options.related_object_type === SCHEMA_TYPE.NATIVE);
  };

  getCustomRelationshipFields = entity => {
    return this.getRelationshipFields(entity)?.filter(field => field.field_options.related_object_type !== SCHEMA_TYPE.NATIVE);
  };

  getNativeRelationshipFieldNames = entity => {
    return this.getNativeRelationshipFields(entity)?.map(field => field.name);
  };

  getNativeRelationshipFieldNameToIdLookup = entity => {
    return this.getNativeRelationshipFields(entity)?.reduce((map, field) => {
      return { ...map, [field.name]: field.related_entity_id };
    }, {});
  };

  // Same intent as getAssociatedNativeObjectIdsForRecord (RecordPageController) but keyed by the raw
  // related_entity_id and without dropping fields absent from nativeObjectData.idLookup - used only to
  // detect datasource native_lookup fields, doesn't touch/replace the existing legacy method.
  getNativeRelationshipIdsBySchemaId = (record, nativeFields) => {
    const idsBySchemaId = {};
    (nativeFields ?? []).forEach(field => {
      if (!record?.[field.name]) return;
      idsBySchemaId[field.related_entity_id] = idsBySchemaId[field.related_entity_id] ?? [];
      idsBySchemaId[field.related_entity_id].push(record[field.name]);
    });
    return idsBySchemaId;
  };

  // Splits {schemaId: ids[]} into the legacy native-schema group (resolved via the existing
  // native-object message-channel mechanism) and the datasource native_lookup group (resolved via the
  // field-resolutions API) - the datasource half is only ever populated under useTypeBasedApi.
  partitionNativeFieldIds = (idsBySchemaId, nativeObjectData) => {
    const legacyIdsByContext = {};
    const datasourceIdsById = {};
    Object.keys(idsBySchemaId ?? {}).forEach(schemaId => {
      if (nativeObjectData?.idLookup?.[schemaId]) {
        legacyIdsByContext[nativeObjectData.idLookup[schemaId].context] = idsBySchemaId[schemaId];
      } else if (shouldUseTypeBasedEntityApi()) {
        datasourceIdsById[schemaId] = idsBySchemaId[schemaId];
      }
    });
    return { legacyIdsByContext, datasourceIdsById };
  };

  // Builds the POST /api/_/datasources/field-resolutions payload from {datasourceId: ids[]}.
  buildFieldResolutionsPayload = datasourceIdsById => {
    return Object.keys(datasourceIdsById ?? {}).map(datasourceId => ({
      type: 'native_lookups',
      datasource_id: String(datasourceId),
      choice_ids: [...new Set(datasourceIdsById[datasourceId])],
    }));
  };

  // Parses the field-resolutions response into a Map<"datasourceId:ref", value>, skipping invalid entries.
  mapFieldResolutionsResponse = apiResponse => {
    const resolutionsByRef = new Map();
    (apiResponse?.result ?? []).forEach(group => {
      (group.resolved_choices ?? []).forEach(choice => {
        if (choice.valid) {
          resolutionsByRef.set(`${group.datasource_id}:${choice.ref}`, choice.value);
        }
      });
    });
    return resolutionsByRef;
  };

  getDateFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.DATE);
  };

  getMulitselectFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.MULTI_SELECT);
  };

  getCheckboxFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.CHECKBOX);
  };

  getParagraphFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.PARAGRAPH);
  };

  getNumericFields = entity => {
    return entity?.fields?.filter(field => field.type === ENTITY_FIELD_TYPE.NUMBER || field.type === ENTITY_FIELD_TYPE.DECIMAL);
  };

  getRelatedEntityIdLookup = fields => {
    return fields?.reduce(function (map, field) {
      return { ...map, [field.name]: field.related_entity_id };
    }, {});
  };

  areFieldsPresent = entity => {
    return entity?.fields?.length > 0;
  };

  __getRecordsByDisplayIds = async (id, displayIds) => {
    if (!this.hasRecordPermission(PERMISSION_TYPE.READ)) throw this.constructErrorObject(LEGO_CONSTANTS.NO_RECORD_READ_PERMISSION);
    return await this.recordService.bulk({ id }, { display_ids: displayIds, include: 'associations' });
  };

  getRecordsInBulk = async entityIdToDisplayIdsLookup => {
    try {
      const promises = [];
      const entityIdRecords = {};
      const entityIds = Object.keys(entityIdToDisplayIdsLookup);
      for (const entityId of entityIds) {
        const displayIds = entityIdToDisplayIdsLookup[entityId];
        if (displayIds.length === 0) continue;
        promises.push(this.__getRecordsByDisplayIds(entityId, displayIds));
      }
      const response = await Promise.allSettled(promises);
      const records = response
        .filter(res => res.status === 'fulfilled')
        .map(res => res.value)
        .reduce((res, array) => [...res, ...(array?.records ?? [])], []);
      const displayIdToRecordLookup = records.reduce((map, record) => {
        return { ...map, [record.display_id]: record };
      }, {});
      for (const entityId of entityIds) {
        const displayIds = entityIdToDisplayIdsLookup[entityId];
        entityIdRecords[entityId] = displayIds.map(displayId => displayIdToRecordLookup[displayId]);
      }
      return this.buildResponse(true, entityIdRecords);
    } catch (error) {
      console.error(error);
      return this.buildErrorResponse(error);
    }
  };

  getDisplayIdToTitleLookup = (entityIdToRecordsLookup, relatedEntities) => {
    const entityIds = Object.keys(entityIdToRecordsLookup);
    const primaryFieldLookup = relatedEntities.reduce((map, entity) => {
      return { ...map, [entity?.id]: CommonUtils.getPrimaryField(entity)?.name };
    }, {});
    let displayIdToTitleLookup = {};
    for (const entityId of entityIds) {
      const records = entityIdToRecordsLookup[entityId];
      const __lookup = records?.reduce((map, record) => {
        const primaryField = primaryFieldLookup[entityId];
        return { ...map, [record?.display_id]: record?.data?.[primaryField] };
      }, {});
      displayIdToTitleLookup = { ...displayIdToTitleLookup, ...__lookup };
    }
    return displayIdToTitleLookup;
  };

  getEnrichRecordData = (
    entity,
    record,
    nativeObjectData,
    translationFn,
    language,
    timezone,
    configType = 'default',
    showRecordId,
    choicesByFieldId = new Map(),
    nativeLookupResolutionsByRef = new Map()
  ) => {
    if (!nativeObjectData.loaded || !record?.data) return { enrichedRecordData: [], recordUpdateModalInitValues: {} };
    const fieldLookup = this.getFieldLookup(entity);
    // The API returns a DROPDOWN_LOOKUP_SECTION field's value flattened across its dependent sub-field
    // keys (<field.name>_dd<level>, see FormRendererController.parseSchemaFields) - re-key it back to a
    // single value under the field's own name before enriching, same as every other field type expects.
    const recordData = normalizeDependentLookupRecordData(entity?.fields, record.data);
    const enrichedRecordData = Object.keys(recordData ?? {})
      ?.sort((r1, r2) => fieldLookup[r1]?.position - fieldLookup[r2]?.position)
      ?.map(fieldName => {
        const field = fieldLookup[fieldName];
        const fieldId = field?.id;
        const fieldLabel = field?.label;
        let actualValue = recordData[fieldName];
        const fieldType = field?.type;
        if (fieldType === ENTITY_FIELD_TYPE.DROPDOWN) {
          actualValue = entity?.translatedEntityChoicesLookup?.get(fieldName)?.[actualValue] ?? actualValue;
        }

        if (fieldType === ENTITY_FIELD_TYPE.MULTI_SELECT) {
          actualValue =
            actualValue?.map(itemValue => {
              return entity?.translatedEntityChoicesLookup?.get(fieldName)?.[itemValue] ?? itemValue;
            }) ?? actualValue;
        }

        const fieldSubType =
          fieldType === ENTITY_FIELD_TYPE.RELATIONSHIP && field.field_options.related_object_type === SCHEMA_TYPE.NATIVE ? SCHEMA_TYPE.NATIVE : SCHEMA_TYPE.CUSTOM;
        const fieldRelatedEntityId = field?.related_entity_id;
        const displayValue = this.getRecordDataDisplayValueByType(
          record,
          fieldRelatedEntityId,
          actualValue,
          fieldType,
          fieldSubType,
          nativeObjectData,
          translationFn,
          language,
          configType,
          field,
          choicesByFieldId,
          nativeLookupResolutionsByRef
        );
        return { fieldId, fieldRelatedEntityId, fieldLabel, fieldName, actualValue, fieldType, fieldSubType, displayValue };
      });
    const recordUpdateModalInitValues = this.parseRecordUpdateModalInitValues(enrichedRecordData);
    if (showRecordId) {
      enrichedRecordData.unshift({
        fieldName: 'record_id',
        fieldLabel: translationFn('general.recordId'),
        actualValue: record.display_id,
        displayValue: record.display_id,
      });
    }
    enrichedRecordData.push({
      fieldName: 'created_at',
      fieldLabel: translationFn('general.createdAt'),
      displayValue: CommonUtils.timeSince(new Date(record.created_time), translationFn, language, timezone),
    });
    enrichedRecordData.push({
      fieldName: 'updated_at',
      fieldLabel: translationFn('general.updatedAt'),
      displayValue: CommonUtils.timeSince(new Date(record.updated_time), translationFn, language, timezone),
    });
    return { enrichedRecordData, recordUpdateModalInitValues };
  };

  getRecordDataDisplayValueByType = (
    record,
    fieldRelatedEntityId,
    actualValue,
    fieldType,
    fieldSubType,
    nativeObjectData,
    translationFn,
    language,
    configType,
    field,
    choicesByFieldId,
    nativeLookupResolutionsByRef = new Map()
  ) => {
    switch (fieldType) {
      case ENTITY_FIELD_TYPE.CHECKBOX:
        return actualValue === true ? translationFn('general.yes') : translationFn('general.no');
      case ENTITY_FIELD_TYPE.NUMBER:
        return actualValue === null ? LEGO_CONSTANTS.EMPTY_DEFAULT_DATA : actualValue;
      case ENTITY_FIELD_TYPE.DECIMAL:
        return actualValue === null ? LEGO_CONSTANTS.EMPTY_DEFAULT_DATA : actualValue;
      case ENTITY_FIELD_TYPE.RELATIONSHIP:
        if (!actualValue) return LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
        if (fieldSubType === SCHEMA_TYPE.NATIVE) {
          if (shouldUseTypeBasedEntityApi() && !nativeObjectData?.idLookup[fieldRelatedEntityId]) {
            const resolvedValue = nativeLookupResolutionsByRef.get(`${fieldRelatedEntityId}:${actualValue}`);
            return { value: resolvedValue ?? actualValue, meta: { source: 'datasource', datasourceId: fieldRelatedEntityId } };
          }
          const context = nativeObjectData?.idLookup[fieldRelatedEntityId] ? nativeObjectData.idLookup[fieldRelatedEntityId]?.context : null;
          const dataToDisplay = getNativeObjectIDForConfigType(configType, actualValue);
          return { value: record?.nativeAssociations?.[context]?.[dataToDisplay] ?? dataToDisplay, meta: context };
        } else {
          const primaryFieldName = record?.associations?.[actualValue]?.metadata.primary_field_name;
          const meta = { ...record?.associations?.[actualValue], display_id: actualValue };
          return { value: record?.associations?.[actualValue]?.data?.[primaryFieldName] ?? actualValue, meta };
        }
      case ENTITY_FIELD_TYPE.DATE:
        return actualValue ? CommonUtils.getFormattedDate(new Date(actualValue), language) : actualValue;
      case ENTITY_FIELD_TYPE.DATE_TIME:
        return actualValue ? CommonUtils.getFormattedDateTime(new Date(actualValue), language) : actualValue;
      case ENTITY_FIELD_TYPE.MULTI_SELECT:
        actualValue = actualValue?.filter(val => val);
        return actualValue?.join(', ');
      case ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP: {
        // The record stores the choice's id (see FormRendererController.getFormSchema), so it's resolved
        // to its display value here rather than in actualValue, which recordUpdateModalInitValues needs
        // to keep as the raw id to correctly re-seed the edit form's dropdown.
        if (actualValue == null || actualValue === '') return LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
        const choices = getLookupChoicesForField(field, choicesByFieldId) ?? [];
        return findChoiceValueById(choices, actualValue) ?? actualValue;
      }
      case ENTITY_FIELD_TYPE.DROPDOWN_LOOKUP_SECTION: {
        // Only the leaf id is persisted, so the full root-to-leaf path is resolved for display here -
        // actualValue is left as the raw leaf id for the same edit-form-reseeding reason as above.
        if (actualValue == null || actualValue === '') return LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
        const choices = getLookupChoicesForField(field, choicesByFieldId) ?? [];
        const valuePath = findDependentChoiceValuePathById(choices, actualValue);
        return valuePath ? valuePath.join(', ') : actualValue;
      }
      default:
        return actualValue ?? LEGO_CONSTANTS.EMPTY_DEFAULT_DATA;
    }
  };

  parseRecordUpdateModalInitValues = (enrichedRecordData, configType = 'default') => {
    return enrichedRecordData?.reduce((map, item) => {
      let value = item.actualValue;
      if (item.fieldType === ENTITY_FIELD_TYPE.RELATIONSHIP) {
        if (item.fieldSubType === SCHEMA_TYPE.NATIVE) {
          if (item.displayValue?.meta?.source === 'datasource') {
            // resolved via the field-resolutions API - displayValue.value is already the plain resolved
            // name (or the raw id as a fallback when unresolved), unlike the legacy shape below.
            value = item.actualValue ? [{ text: String(item.displayValue?.value ?? item.actualValue), value: item.actualValue }] : null;
          } else {
            const configs = nativeObjectConfigs[this.configType ?? configType][item.displayValue.meta];
            if (item?.displayValue?.value && item?.displayValue?.value?.[configs?.defaultDisplayField] && item.actualValue) {
              value = [{ text: item.displayValue?.value?.[configs.defaultDisplayField], value: item.actualValue }];
            } else {
              value = null;
            }
          }
        } else {
          if (item?.displayValue?.value && item.actualValue) {
            value = [{ text: item.displayValue.value, value: item.actualValue }];
          } else {
            value = null;
          }
        }
      }
      if (Array.isArray(value)) {
        value = value.filter(iterm => iterm); // for ommitting null values in multi-select
      }
      return { ...map, [item.fieldName]: value };
    }, {});
  };
}
